import React, { useContext, useState, useEffect, useMemo } from 'react';
import { Context as AuthContext } from 'contexts/auth';
import { Context as AlertContext } from 'contexts/alert';
import { useForm } from 'react-hook-form';
import { Config } from 'configs';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { code as cmCode } from 'codemash';
import { saveRKeeperCredentials } from 'api/restaurant';
import { FormButton } from 'components/buttons/formButton';
import { DropDownList } from 'components/formInputs/dropDownList';
import { Loader } from 'components/indicators/loader';
import { CheckboxWrapper } from 'components/formInputs/wrappers/checkboxWrapper';
import { apiActionChooser } from 'utils/apiActionChooser';
import { functionRequest } from 'api/client/requests';

export const RestaurantIntegrationIiko = ({
  restaurant,
  integrationSelectionBlock,
  onSaved,
  loading,
  setupData,
}) => {
  const { state: auth } = useContext(AuthContext);
  const { alertController } = useContext(AlertContext);

  const [productsSyncStarted, setProductsSyncStarted] = useState(false);
  const [tablesSyncStarted, setTablesSyncStarted] = useState(false);
  const [discountsSyncStarted, setDiscountsSyncStarted] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [isUsingDeliveryApi, setIsUsingDeliveryApi] = useState(setupData?.useDeliveryApi || false);

  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [terminalGroupOptions, setTerminalGroupOptions] = useState([]);
  const [paymentsOptions, setPaymentsOptions] = useState([]);
  const [menuOptions, setMenuOptions] = useState([]);
  const [priceCategoryOptions, setPriceCategoryOptions] = useState([]);
  const [takeawayTypeOptions, setTakeawayTypeOptions] = useState([]);
  const [deliveryTypeOptions, setDeliveryTypeOptions] = useState([]);

  const schema = yup.object().shape({
    organization_id: yup.string().default(setupData?.organizationId),
    terminal_group: yup.string().default(setupData?.terminalGroup),
    payment_id: yup.string().default(setupData?.paymentId),
    menu_id: yup.string().default(setupData?.menuId),
    price_category_id: yup.string().default(setupData?.priceCategoryId),
    use_delivery_api: yup.bool().default(setupData?.useDeliveryApi || false),
    takeout_order_type: yup.string().default(setupData?.takeoutOrderType),
    delivery_order_type: yup.string().default(setupData?.deliveryOrderType),
  });

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  const onSelectOrganization = (val) => {
    setValue('terminal_group', '');
    setValue('payment_id', '');
    setValue('takeout_order_type', '');
    setValue('delivery_order_type', '');

    setTerminalGroupOptions([
      { value: '', label: '-' },
      ...(setupData?.terminals?.find((x) => x.organizationId === val)?.items || []).map((x) => ({
        value: x.id,
        label: x.name,
      })),
    ]);

    setDeliveryTypeOptions([
      { value: '', label: '-' },
      ...(setupData?.orderTypes?.find((x) => x.organizationId === val)?.items || [])
        .filter((x) => x.orderServiceType !== 'Common' && !x.isDeleted)
        .map((x) => ({
          value: x.id,
          label: x.name,
          orderServiceType: x.orderServiceType,
        })),
    ]);

    setTakeawayTypeOptions([
      { value: '', label: '-' },
      ...(setupData?.orderTypes?.find((x) => x.organizationId === val)?.items || [])
        .filter(
          (x) =>
            x.orderServiceType !== 'Common' &&
            x.orderServiceType !== 'DeliveryByCourier' &&
            !x.isDeleted,
        )
        .map((x) => ({
          value: x.id,
          label: x.name,
          orderServiceType: x.orderServiceType,
        })),
    ]);

    setPaymentsOptions([{ value: '', label: '-' }]);
  };

  const onSelectTerminalGroup = (val) => {
    setValue('payment_id', '');

    setPaymentsOptions([
      { value: '', label: '-' },
      ...(
        setupData?.paymentTypes?.filter(
          (x) => x.terminalGroups && x.terminalGroups.find((y) => y.id === val),
        ) || []
      ).map((x) => ({ value: x.id, label: x.name })),
    ]);
  };

  const onInitOptions = () => {
    setIsUsingDeliveryApi(setupData?.useDeliveryApi || false);

    setOrganizationOptions([
      { value: '', label: '-' },
      ...(setupData?.organizations || []).map((x) => ({ value: x.id, label: x.name })),
    ]);

    setTerminalGroupOptions([
      { value: '', label: '-' },
      ...(
        setupData?.terminals?.find((x) => x.organizationId === setupData?.organizationId)?.items ||
        []
      ).map((x) => ({ value: x.id, label: x.name })),
    ]);

    setPaymentsOptions([
      { value: '', label: '-' },
      ...(
        setupData?.paymentTypes?.filter(
          (x) =>
            x.terminalGroups && x.terminalGroups.find((y) => y.id === setupData?.terminalGroup),
        ) || []
      ).map((x) => ({ value: x.id, label: x.name })),
    ]);

    setMenuOptions([
      { value: '', label: 'Internal' },
      ...(setupData?.externalMenus || []).map((x) => ({ value: x.id, label: x.name })),
    ]);

    setPriceCategoryOptions([
      { value: '', label: '-' },
      ...(setupData?.priceCategories || []).map((x) => ({ value: x.id, label: x.name })),
    ]);

    setDeliveryTypeOptions([
      { value: '', label: '-' },
      ...(
        setupData?.orderTypes?.find((x) => x.organizationId === setupData?.organizationId)?.items ||
        []
      )
        .filter((x) => x.orderServiceType !== 'Common' && !x.isDeleted)
        .map((x) => ({ value: x.id, label: x.name, orderServiceType: x.orderServiceType })),
    ]);

    setTakeawayTypeOptions([
      { value: '', label: '-' },
      ...(
        setupData?.orderTypes?.find((x) => x.organizationId === setupData?.organizationId)?.items ||
        []
      )
        .filter(
          (x) =>
            x.orderServiceType !== 'Common' &&
            x.orderServiceType !== 'DeliveryByCourier' &&
            !x.isDeleted,
        )
        .map((x) => ({ value: x.id, label: x.name, orderServiceType: x.orderServiceType })),
    ]);
  };

  const resetCredentials = () => {
    if (setupData) {
      reset({
        organization_id: setupData.organizationId,
        terminal_group: setupData.terminalGroup,
        payment_id: setupData.paymentId,
        menu_id: setupData.menuId,
        price_category_id: setupData.priceCategoryId,
        use_delivery_api: setupData.useDeliveryApi,
        takeout_order_type: setupData.takeoutOrderType,
        delivery_order_type: setupData.deliveryOrderType,
      });

      onInitOptions();
    } else {
      reset({});

      onInitOptions();
    }
  };

  useEffect(() => {
    resetCredentials();
  }, [setupData, reset]);

  const onSubmit = async (data) => {
    setIsSaving(true);

    const finalCredentials = {
      ...data,
      integrationType: 'iiko',
    };

    const paymentData = setupData?.paymentTypes?.find((x) => x.id === finalCredentials.payment_id);

    if (paymentData) {
      finalCredentials.payment_type = paymentData.paymentTypeKind;
    } else {
      finalCredentials.payment_type = '';
    }

    const response = await saveRKeeperCredentials({
      token: auth.token,
      restaurantId: restaurant._id,
      credentials: finalCredentials,
      alertController,
      companyId: auth.company?.company,
    });

    if (response && response.saved) {
      alertController.success({ text: 'Iiko integration saved' });

      onSaved('iiko', data);
      setIsEditing(false);
    } else if (response && response.saved === false) {
      alertController.error({ text: 'Failed to save Iiko integration' });
    }

    setIsSaving(false);
  };

  const onError = (data) => console.log(data);

  const onSyncProducts = async () => {
    if (!restaurant?._id) {
      return;
    }

    setProductsSyncStarted(true);

    try {
      await apiActionChooser(
        auth.company?.company,
        async () => {
          await cmCode.executeFunction({
            secretKey: auth.token,
            functionId: Config.DATA.FUNCTIONS.SYNC_PRODUCTS,
            data: { token: auth.token, restaurantId: restaurant._id, resource: 'products' },
          });
        },
        async () => {
          await functionRequest({
            functionName: 'SyncIntegrationResource',
            data: JSON.stringify({ restaurantId: restaurant._id, resource: 'products' }),
          });
        },
      );

      alertController.success({ text: 'Product sync started' });
    } catch (e) {
      alertController.error({ text: 'Failed to sync products' });
    }

    setProductsSyncStarted(false);
  };

  const onSyncTables = async () => {
    if (!restaurant?._id) {
      return;
    }

    setTablesSyncStarted(true);

    try {
      await apiActionChooser(
        auth.company?.company,
        async () => {
          await cmCode.executeFunction({
            secretKey: auth.token,
            functionId: Config.DATA.FUNCTIONS.SYNC_PRODUCTS,
            data: { token: auth.token, restaurantId: restaurant._id, resource: 'tables' },
          });
        },
        async () => {
          await functionRequest({
            functionName: 'SyncIntegrationResource',
            data: JSON.stringify({ restaurantId: restaurant._id, resource: 'tables' }),
          });
        },
      );

      alertController.success({ text: 'Tables sync started' });
    } catch (e) {
      alertController.error({ text: 'Failed to sync products' });
    }

    setTablesSyncStarted(false);
  };

  const onSyncDiscounts = async () => {
    if (!restaurant?._id) {
      return;
    }

    setDiscountsSyncStarted(true);

    try {
      await apiActionChooser(
        auth.company?.company,
        async () => {
          await cmCode.executeFunction({
            secretKey: auth.token,
            functionId: Config.DATA.FUNCTIONS.SYNC_PRODUCTS,
            data: { token: auth.token, restaurantId: restaurant._id, resource: 'discounts' },
          });
        },
        async () => {
          await functionRequest({
            functionName: 'SyncIntegrationResource',
            data: JSON.stringify({ restaurantId: restaurant._id, resource: 'discounts' }),
          });
        },
      );

      alertController.success({ text: 'Discounts sync started' });
    } catch (e) {
      alertController.error({ text: 'Failed to sync discounts' });
    }

    setDiscountsSyncStarted(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} action="#" method="POST">
      {integrationSelectionBlock}

      {loading && (
        <div className="mt-6">
          <Loader small />
        </div>
      )}

      {!loading && (
        <div className="grid grid-cols-6 gap-6 mt-6">
          <div className="col-span-6 sm:col-span-3">
            <label htmlFor="organization_id" className="block text-sm font-medium text-gray-700">
              Organization
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <DropDownList
                {...register('organization_id')}
                id="organization_id"
                options={organizationOptions}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                onChange={(e) => onSelectOrganization(e.target.value)}
                disabled={!isEditing}
              />
            </div>
            {errors?.organization_id && (
              <p className="mt-2 text-sm text-red-600" id="organization_id-error">
                {errors?.organization_id.message}
              </p>
            )}
          </div>
          <div className="col-span-6 sm:col-span-3">
            <label htmlFor="terminal_group" className="block text-sm font-medium text-gray-700">
              Terminal group
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <DropDownList
                {...register('terminal_group')}
                id="terminal_group"
                options={terminalGroupOptions}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                onChange={(e) => onSelectTerminalGroup(e.target.value)}
                disabled={!isEditing}
              />
            </div>
            {errors?.terminal_group && (
              <p className="mt-2 text-sm text-red-600" id="terminal_group-error">
                {errors?.terminal_group.message}
              </p>
            )}
          </div>
          <div className="col-span-6 sm:col-span-3">
            <label htmlFor="payment_id" className="block text-sm font-medium text-gray-700">
              Payment
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <DropDownList
                {...register('payment_id')}
                id="payment_id"
                options={paymentsOptions}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                disabled={!isEditing}
              />
            </div>
            {errors?.payment_id && (
              <p className="mt-2 text-sm text-red-600" id="payment_id-error">
                {errors?.payment_id.message}
              </p>
            )}
          </div>
          <div className="col-span-6 sm:col-span-3">
            <label htmlFor="menu_id" className="block text-sm font-medium text-gray-700">
              Menu
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <DropDownList
                {...register('menu_id')}
                id="menu_id"
                options={menuOptions}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                disabled={!isEditing}
              />
            </div>
            {errors?.menu_id && (
              <p className="mt-2 text-sm text-red-600" id="menu_id-error">
                {errors?.menu_id.message}
              </p>
            )}
          </div>
          <div className="col-span-6 sm:col-span-3">
            <label htmlFor="price_category_id" className="block text-sm font-medium text-gray-700">
              Price Category (Might be required for non-internal menus)
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <DropDownList
                {...register('price_category_id')}
                id="price_category_id"
                options={priceCategoryOptions}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                disabled={!isEditing}
              />
            </div>
            {errors?.price_category_id && (
              <p className="mt-2 text-sm text-red-600" id="price_category_id-error">
                {errors?.price_category_id.message}
              </p>
            )}
          </div>

          <CheckboxWrapper
            register={register}
            name="use_delivery_api"
            title="Use delivery API"
            subtitle="uses delivery APIs provided by Iiko"
            responsiveClassName="col-span-6"
            onChange={(e) => {
              setIsUsingDeliveryApi(e.target.checked);
            }}
            disabled={!isEditing}
          />

          {isUsingDeliveryApi && (
            <>
              <div className="col-span-6 sm:col-span-3">
                <label
                  htmlFor="takeout_order_type"
                  className="block text-sm font-medium text-gray-700"
                >
                  Takeout type
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <DropDownList
                    {...register('takeout_order_type')}
                    id="takeout_order_type"
                    options={takeawayTypeOptions}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    disabled={!isEditing}
                  />
                </div>
                {errors?.takeout_order_type && (
                  <p className="mt-2 text-sm text-red-600" id="takeout_order_type-error">
                    {errors?.takeout_order_type.message}
                  </p>
                )}
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label
                  htmlFor="delivery_order_type"
                  className="block text-sm font-medium text-gray-700"
                >
                  Delivery type
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <DropDownList
                    {...register('delivery_order_type')}
                    id="delivery_order_type"
                    options={deliveryTypeOptions}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    disabled={!isEditing}
                  />
                </div>
                {errors?.delivery_order_type && (
                  <p className="mt-2 text-sm text-red-600" id="delivery_order_type-error">
                    {errors?.delivery_order_type.message}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {!loading && (
        <div className="mt-4">
          <FormButton
            text={isEditing ? 'Cancel' : 'Edit'}
            noMargin
            outline
            onClick={() => {
              if (isEditing) {
                resetCredentials();
              }

              setIsEditing(!isEditing);
            }}
          />

          {isEditing && <FormButton text="Save" isLoading={isSaving} />}
        </div>
      )}

      {!loading && (
        <fieldset>
          <div className="mt-8">
            <legend className="text-base font-medium text-gray-900">Import data</legend>
            <p className="text-sm text-gray-500">Import data from your integration into Waitless</p>
          </div>
          <div className="flex flex-row flex-wrap">
            <div className="mr-4 mt-4">
              <FormButton
                text="Import tables"
                isLoading={tablesSyncStarted}
                disabled={productsSyncStarted || tablesSyncStarted || discountsSyncStarted}
                onClick={onSyncTables}
                noMargin
              />
            </div>
            <div className="mr-4 mt-4">
              <FormButton
                text="Import products"
                isLoading={productsSyncStarted}
                disabled={productsSyncStarted || tablesSyncStarted || discountsSyncStarted}
                onClick={onSyncProducts}
                noMargin
              />
            </div>
            {/* <div className="mr-4 mt-4">
              <FormButton
                text="Import discounts"
                isLoading={discountsSyncStarted}
                disabled={productsSyncStarted || tablesSyncStarted || discountsSyncStarted}
                onClick={onSyncDiscounts}
                noMargin
              />
            </div> */}
          </div>
        </fieldset>
      )}
    </form>
  );
};
